import type { Bonus, CollageItem, FaqItem, Feature, Place, Printable, Step, Testimonial } from '../types/content'

export const productName = 'Gospel Sharing Mega Bundle'
export const checkoutUrl = 'https://the-gospel-sharing-kit.impultienda.ar/checkout'
export const offerAnchor = '#offer'

/** Finished main product mockup — used once, in the hero. */
export const heroMockup = 'https://cdn.magicpatterns.com/uploads/2vj8c9qfY99QBqGmGnpJJc/MOCKUP_(2).png'

/* ---------- Section 2 — Conviction ---------- */
export const hesitations = [
  'What do I say?',
  'What if they reject me?',
  'I’m too shy.',
  'I don’t know enough about the Bible.',
  'I don’t want to make things awkward.',
  'I’ll do it next time.',
]

/* ---------- Section 3 — Solution ---------- */
export const simpleMessages = ['A Bible verse.', 'A message of hope.', 'A reminder of God’s love.', 'A simple message that points someone toward Jesus.']
export const handoffs = ['hand to someone…', 'include with a gift…', 'share with a coworker…', 'give to a neighbor…', 'or carry with you so you’re ready when an opportunity comes.']
export const noMores = ['No complicated preparation.', 'No wondering what to create.', 'No waiting until you finally feel “ready.”']

/* ---------- Section 4 — How it works ---------- */
export const steps: Step[] = [
  {
    title: 'Print',
    text: 'Choose the Bible verse or Gospel-sharing design you want to use and print it from home or at your favorite print shop.',
  },
  {
    title: 'Cut',
    text: 'Cut out the cards, notes or printable messages you want to share.',
    extras: ['No designing from scratch.', 'No complicated setup.', 'Everything is already prepared for you.'],
  },
  {
    title: 'Share',
    text: 'Carry them with you and use them whenever God gives you an opportunity to plant a seed.',
    extras: ['One small message may reach someone at exactly the moment they need it.'],
  },
]

/* ---------- Section 5 — Material preview ---------- */
export const printables: Printable[] = [
  {
    id: 'butterfly',
    src: 'https://cdn.magicpatterns.com/uploads/2ZnKpKVVcSnXx5Y4tPsfzE/Captura_de_pantalla_2026-10-01_104603.png',
    alt: 'Blue butterfly card titled A Message from God for You with 1 John 4:10',
    wide: false,
  },
  {
    id: 'roses',
    src: 'https://cdn.magicpatterns.com/uploads/tr6QcnHiPCimDCVVc9oR3u/Captura_de_pantalla_2026-10-01_101655.png',
    alt: 'Watercolor roses printable card with Romans 10:13',
    wide: true,
  },
  {
    id: 'globe',
    src: 'https://cdn.magicpatterns.com/uploads/jte1FeLhWRB8XWTDytDqrD/Captura_de_pantalla_2026-10-01_101633.png',
    alt: 'Illustrated card of Jesus holding the world with Jeremiah 31:3',
    wide: false,
  },
  {
    id: 'message',
    src: 'https://cdn.magicpatterns.com/uploads/fzgK1B8Lcapeik12eyzmw5/Captura_de_pantalla_2026-10-01_101612.png',
    alt: 'God Has a Message for You card with John 6:37',
    wide: true,
  },
  {
    id: 'lamb',
    src: 'https://cdn.magicpatterns.com/uploads/qTzEBhQYweNZ62U1g4mryi/Captura_de_pantalla_2026-10-01_101516.png',
    alt: 'Watercolor lamb card with Psalm 23:3',
    wide: true,
  },
  {
    id: 'sign',
    src: 'https://cdn.magicpatterns.com/uploads/mWpwBQpPkKYQxv3Hvs6LyL/Captura_de_pantalla_2026-10-01_101438.png',
    alt: 'Road sign style card with John 3:16',
    wide: false,
  },
]

export const themes = ['God’s Love', 'Hope', 'Prayer', 'Peace', 'Salvation', 'Jesus', 'The Cross', 'God’s Promises', 'Encouragement', 'Faith']

/* ---------- Section 6 — Everyday evangelism ---------- */
export const places: Place[] = [
  { title: 'At Work', text: 'With coworkers or people you meet throughout your day.' },
  { title: 'In Your Neighborhood', text: 'With neighbors and people in your local community.' },
  { title: 'With Gifts', text: 'Add a Gospel card or Bible verse to a gift, package or act of kindness.' },
  { title: 'With Friends and Family', text: 'Share a message that can naturally open the door to a deeper conversation about Jesus.' },
  { title: 'During Church Outreach', text: 'Keep ready-to-print Scripture resources available for evangelism, community outreach and church activities.' },
  { title: 'In Everyday Encounters', text: 'Carry a few with you so that when an opportunity appears, you already have something meaningful to share.' },
]

/* ---------- Section 7 — Shyness ---------- */
export const evangelismMyths = [
  'People who can preach boldly.',
  'People who know the Bible inside and out.',
  'People who are naturally outgoing.',
  'People who always know exactly what to say.',
]
export const firstSteps = ['One verse.', 'One message.', 'One reminder of Jesus.']

/* ---------- Section 8 — What you get ---------- */
export const featureHighlights: (Feature & { value: string })[] = [
  {
    value: '200+',
    title: 'Bible Verses Ready to Share',
    text: 'A large collection of Scripture-centered messages selected to help you share God’s Word, hope and the Gospel in everyday situations.',
  },
  {
    value: '20+',
    title: 'Different Printable Gospel Designs',
    text: 'More than 20 distinct Gospel-sharing designs and formats so you have different ways to share Scripture depending on the person, place or opportunity.',
  },
]
export const features: Feature[] = [
  { title: 'Multiple Gospel Themes', text: 'Messages centered around hope, salvation, prayer, God’s love, faith, encouragement, Jesus, the Cross and more.' },
  { title: 'Ready-to-Print PDF Files', text: 'No design software required. Simply choose the resource you want, print it, cut it and share it.' },
  { title: 'Print Again Whenever You Need More', text: 'Your digital files stay with you. Whenever you need more, simply print another batch.' },
  { title: 'Instant Digital Access', text: 'No shipping. No waiting. Get access after your purchase and start preparing today.' },
]

/* ---------- Section 9 — Purpose ---------- */
export const purposeUses = ['To be printed.', 'To be carried with you.', 'To be shared.']
export const peopleNeeds = ['A word of hope.', 'A reminder that God sees them.', 'A Bible verse.', 'An invitation to church.', 'Or simply someone willing to plant the first seed.']

/* ---------- Section 10 — Bonuses ---------- */
export const bonuses: Bonus[] = [
  {
    number: 1,
    name: '50 Gospel Conversation Starters',
    lead: 'Not sure what to say after someone shows interest?',
    body: 'Use these simple conversation starters to help you naturally move from an everyday conversation toward faith, Jesus and the Gospel.',
    coverImage: 'https://cdn.magicpatterns.com/uploads/jY1ENULtbfX6p1WkWMpr1D/50_iniciadores_de_conversacion_gospel.png',
  },
  {
    number: 2,
    name: 'Short Prayer Guide After Sharing',
    lead: 'You planted the seed. Now pray over it.',
    body: 'Use this simple prayer guide to help you pray for the people you encounter, the messages you share and the seeds you’ve planted.',
    coverImage: 'https://cdn.magicpatterns.com/uploads/28ZuiqdxhbyDgxeLoA9jyk/Guia_de_oracion_al_amanecer.png',
  },
  {
    number: 3,
    name: 'Church Invitation Kit',
    lead: 'Ready-to-use printable invitations that help you give someone a simple next step: Come to church.',
    body: 'Perfect for inviting friends, neighbors, coworkers and people you meet during outreach.',
    coverImage: 'https://cdn.magicpatterns.com/uploads/r9z4A2E8rHDpoQTX4N3tiq/3d406038-7340-4293-a6a1-1253bee7c13d.png',
  },
  {
    number: 4,
    name: 'Gospel Message Match Guide',
    lead: 'Wondering which message to share?',
    body: 'Use this quick-reference guide to help you choose a Gospel message based on what someone may be going through — hope, fear, discouragement, prayer, God’s love, salvation and more.',
    coverImage: 'https://cdn.magicpatterns.com/uploads/76NzgMaHmTk8ni1CZ8BH8v/Guia_de_mensajes_del_evangelio.png',
  },
  {
    number: 5,
    name: '30-Day Gospel Sharing Challenge',
    lead: 'Turn evangelism from something you keep postponing into something you intentionally practice.',
    body: 'Follow a simple 30-day challenge designed to encourage you to look for opportunities, plant seeds and make Gospel sharing part of your everyday Christian life.',
    coverImage: 'https://cdn.magicpatterns.com/uploads/cF6WoDm6AChS8K46fjqF6j/Desafio_Gospel_de_30_Dias.png',
  },
]

/* ---------- Section 11 — Reframe ---------- */
export const ordinaryPlaces = [
  { place: 'The grocery store', rest: 'is no longer just the grocery store.' },
  { place: 'Your workplace', rest: 'is no longer just your workplace.' },
  { place: 'Your neighborhood', rest: 'is no longer just where you live.' },
]
export const someoneMay = ['Someone may be praying for hope.', 'Someone may feel forgotten.', 'Someone may be searching for God.', 'Someone may need to hear about Jesus.']

/* ---------- Section 12 — Social proof ---------- */
export const testimonials: Testimonial[] = [
  {
    headline: 'I finally stopped overthinking how to start.',
    quote:
      'I’ve wanted to share my faith more often, but I would always freeze because I didn’t know what to say. Having these messages already prepared made it feel so much easier to take that first step. I printed a few, put them in my purse, and now I actually look for opportunities to share them.',
    name: 'Sarah M.',
    role: 'Christian Mom',
  },
  {
    headline: 'Now I always have something with me to share.',
    quote:
      'I printed several of the designs and keep a small stack in my car and another in my bag. I love knowing that if I meet someone who needs encouragement, I already have a Bible verse ready to give them. It’s such a simple way to be more intentional about sharing God’s Word.',
    name: 'Rebecca T.',
    role: 'Church Member',
  },
  {
    headline: 'We’ve been using them for outreach and gifts.',
    quote:
      'These have been so useful for our church outreach. We’ve included them with small gifts, handed them out during community activities, and shared them with visitors. They make it easy to add a meaningful Gospel message without having to create everything ourselves.',
    name: 'Daniel R.',
    role: 'Ministry Volunteer',
  },
  {
    headline: 'There’s always a message that fits the moment.',
    quote:
      'What surprised me most was the variety. With 200+ Bible verses and more than 20 different designs, I’m not giving everyone the exact same message. I can choose something about hope, prayer, God’s love, faith, or salvation depending on the person and situation.',
    name: 'Michelle A.',
    role: 'Women’s Ministry Leader',
  },
]

/* ---------- Section 13 — Transition ---------- */
export const noNeeds = ['You don’t need to become a street preacher overnight.', 'You don’t need to memorize the perfect speech.', 'You don’t need to have every answer.']
export const oneSeed = ['One verse.', 'One message.', 'One person.', 'One seed.']

/* ---------- Section 14 — Complete offer ---------- */
export const offerIncludes = [
  '200+ Bible Verses Ready to Share',
  '20+ Different Printable Gospel-Sharing Designs',
  'Messages about hope, salvation, prayer, God’s love, faith, Jesus and more',
  'Ready-to-print PDF files',
  'Print again whenever you need more',
  'Instant digital access',
]

/** Real printables used in the offer collage. */
export const collagePrintables: CollageItem[] = [
  { src: 'https://cdn.magicpatterns.com/uploads/ntaKAtoqbqdciapmGtrWpd/Captura_de_pantalla_2026-10-01_101655.png', alt: 'Watercolor roses card with Romans 10:13' },
  { src: 'https://cdn.magicpatterns.com/uploads/bzdhvDa8EmftVLKUM3w9j7/Captura_de_pantalla_2026-10-01_104603.png', alt: 'Blue butterfly card with 1 John 4:10' },
  { src: 'https://cdn.magicpatterns.com/uploads/xwCeiSA3Pzg9vqLdzEr1TT/Captura_de_pantalla_2026-10-01_101612.png', alt: 'God Has a Message for You card with John 6:37' },
  { src: 'https://cdn.magicpatterns.com/uploads/jAKdNV2DNkuHubQ2J68sNX/Captura_de_pantalla_2026-10-01_101633.png', alt: 'Jesus holding the world card with Jeremiah 31:3' },
  { src: 'https://cdn.magicpatterns.com/uploads/gkS7aRXwDmdUh6h7MM6Kt1/Captura_de_pantalla_2026-10-01_104714.png', alt: 'Blue Bible card with Psalm 18' },
  { src: 'https://cdn.magicpatterns.com/uploads/syHCa192HNoiVTwMQGsCQ5/Captura_de_pantalla_2026-10-01_104641.png', alt: 'Praying hands card with Philippians 4:6' },
  { src: 'https://cdn.magicpatterns.com/uploads/hLKe6dVbQH9TBjVRuLnvuz/Captura_de_pantalla_2026-10-01_101516.png', alt: 'Watercolor lamb card with Psalm 23:3' },
  { src: 'https://cdn.magicpatterns.com/uploads/rJQXUMn1yCFV8gNhFR7vUZ/Captura_de_pantalla_2026-10-01_104917.png', alt: 'Jesus card with John 3:16' },
  { src: 'https://cdn.magicpatterns.com/uploads/7ahiHGS4wgvEsYByb6xSA6/Captura_de_pantalla_2026-10-01_104816.png', alt: 'Red roses card with Isaiah 45:22' },
]

/* ---------- Section 16 — FAQ ---------- */
export const faqs: FaqItem[] = [
  { question: 'Is this a physical product?', answer: 'No.\n\nThis is a 100% digital product. You’ll receive digital PDF files that you can download and print yourself.' },
  { question: 'How do I receive the bundle?', answer: 'After completing your purchase, you’ll receive instant digital access to your materials.\n\nThere is no physical shipping.' },
  { question: 'What exactly is included?', answer: 'You’ll receive 200+ Bible verses across 20+ different printable Gospel-sharing designs, plus the 5 included evangelism bonuses.' },
  { question: 'Are the files ready to print?', answer: 'Yes.\n\nThe resources come already designed and prepared in PDF format so you can print, cut and begin sharing.' },
  { question: 'Can I edit the designs?', answer: 'No.\n\nThe designs are not editable. They come finished and ready to print so you don’t need to spend time designing or changing anything.' },
  { question: 'Can I print them more than once?', answer: 'Yes.\n\nOnce you receive your files, you can print new copies whenever you need more for your personal Gospel sharing and permitted ministry use.' },
  { question: 'Do I need special software?', answer: 'No.\n\nYou simply need a device that can open PDF files and access to a printer or print shop.' },
  {
    question: 'Is this only for pastors or church leaders?',
    answer:
      'No.\n\nThe Gospel Sharing Mega Bundle was created for everyday Christians who want a simple way to begin sharing their faith. You don’t need to be a pastor, missionary or experienced evangelist.',
  },
  {
    question: 'What if I’m shy or nervous about evangelizing?',
    answer:
      'That’s exactly why these resources can be so helpful.\n\nYou don’t have to begin with a long conversation or know exactly what to say. Start small. Share a verse. Give someone a Gospel message. Plant a seed.',
  },
  {
    question: 'Can churches use these resources for outreach?',
    answer: 'The resources can be helpful for personal evangelism, ministry activities and outreach according to the usage rights included with your purchase.',
  },
  {
    question: 'What kinds of Bible verses and messages are included?',
    answer:
      'You’ll find Scripture-centered messages related to Jesus, salvation, God’s love, hope, prayer, peace, encouragement, faith, God’s promises, the Cross and more.\n\nWith 200+ Bible verses and 20+ different designs, you’ll have different messages available for different people and situations.',
  },
  { question: 'Is there a guarantee?', answer: 'Yes.\n\nYour purchase is protected by a 7-Day Guarantee.' },
]

/* ---------- Section 17 — Final close ---------- */
export const alreadyGo = ['You already go to the grocery store.', 'You already go to work.', 'You already see neighbors.', 'You already meet people.']
